/**
 * Expanded detail for an org-wide `BatchIngested` summary root. Renders the
 * per-portfolio breakdown (one row per affected sales person / operator /
 * agent) with a booking drill-down link, so admins get the "org-wide metric →
 * specific granular" view the scoped roots give each owner on their My Feed.
 */
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

import { formatFeedPartyLabel } from "./feedViewerParty";
import { SensitiveText } from "@/components/common/Sensitive";
import type { BatchIngestedPayload } from "@/types/feed";
import { SCOPE_TAG_LABEL } from "./feedVisuals";
import { portfolioBookingsHref } from "./feedLinks";

/** Whether a payload is an org-wide BatchIngested summary with a breakdown. */
export function isBatchOrgPayload(
  payload: unknown,
): payload is BatchIngestedPayload {
  if (!payload || typeof payload !== "object") return false;
  const p = payload as Record<string, unknown>;
  return (
    p.kind === "batch" &&
    p.scope === "org" &&
    Array.isArray(p.breakdown) &&
    p.breakdown.length > 0
  );
}

export function BatchIngestedView({
  payload,
}: {
  payload: BatchIngestedPayload;
}) {
  const rows = payload.breakdown ?? [];

  return (
    <div className="feed-row-detail-body">
      <p className="feed-row-detail-body__heading">
        {rows.length} affected portfolio{rows.length === 1 ? "" : "s"}
      </p>
      {rows.map((row) => {
        const href = portfolioBookingsHref(row.dimension, row.key);
        const label = SCOPE_TAG_LABEL[row.dimension] ?? row.dimension;
        return (
          <div
            key={`${row.dimension}:${row.key}`}
            className="flex min-w-0 items-center justify-between gap-2 rounded-lg bg-background/40 px-2.5 py-2"
          >
            <div className="min-w-0">
              <p className="truncate text-xs font-medium leading-tight">
                <span className="text-muted-foreground">{label} · </span>
                <SensitiveText
                  value={formatFeedPartyLabel(row.dimension, row.key)}
                />
              </p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                {row.items} row{row.items === 1 ? "" : "s"}
              </p>
            </div>
            {href && (
              <Link
                to={href}
                className="inline-flex shrink-0 items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary transition-colors hover:bg-primary/20"
              >
                View bookings
                <ArrowUpRight className="size-2.5" />
              </Link>
            )}
          </div>
        );
      })}
    </div>
  );
}
